// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {installPrivyActions, privyAppId} from '@app/features/auth/privy/PrivyMode';
import {clearPendingPrivyLogin} from '@app/features/auth/privy/PrivyPendingLogin';
import {connectPrivyBusinessWallet} from '@app/features/auth/privy/PrivyWalletConnection';
import {findBusinessWallet} from '@app/features/auth/privy/PrivyWalletPolicy';
import {DaoIWallet, type InjectedWallet} from '@app/features/daoi/bridge/DaoIWallet';
import SessionManager from '@app/features/platform/state/AuthSession';
import {PrivyProvider, usePrivy, useWallets} from '@privy-io/react-auth';
import {observer} from 'mobx-react-lite';
import {createContext, type ReactNode, useContext, useEffect, useLayoutEffect, useRef} from 'react';
import {arbitrum, base, bsc, linea, mainnet, polygon, xLayer} from 'viem/chains';

const config = {
	loginMethods: ['email'] as ['email'],
	externalWallets: {disableAllExternalWallets: true, walletConnect: {enabled: false}},
	defaultChain: bsc,
	supportedChains: [bsc, mainnet, base, arbitrum, linea, polygon, xLayer],
	embeddedWallets: {ethereum: {createOnLogin: 'off' as const}, showWalletUIs: true},
};
const PrivyAppContext = createContext<string | null>(null);

// Instance selection can target a different server before Accounts activates it.
// In that case only the sign-in surface needs a temporary provider.
export function PrivyAuthBoundary({appId, children}: {appId: string; children: ReactNode}) {
	const rootAppId = useContext(PrivyAppContext);
	if (rootAppId === appId) return <>{children}</>;
	return (
		<PrivyProvider appId={appId} config={config}>
			<PrivyAppContext value={appId}>{children}</PrivyAppContext>
		</PrivyProvider>
	);
}

// This root stays above Router/AppWrapper/LiveKit for the entire instance session.
export const PrivyRoot = observer(({children}: {children: ReactNode}) => {
	const appId = privyAppId(RuntimeConfig.getSnapshotOrNull());
	if (!appId) return <>{children}</>;
	return (
		<PrivyProvider appId={appId} config={config}>
			<PrivyAppContext value={appId}>
				<PrivySessionBridge />
				{children}
			</PrivyAppContext>
		</PrivyProvider>
	);
});

const PrivySessionBridge = observer(() => {
	const {logout, ready, authenticated} = usePrivy();
	// Aggregate readiness also waits for the external connectors disabled above.
	const {wallets} = useWallets();
	const token = SessionManager.token;
	const previousToken = useRef(token);
	const boundAddress = DaoIWallet.boundAddress;
	const wallet = findBusinessWallet(wallets, boundAddress);
	useLayoutEffect(() => {
		DaoIWallet.setEmbeddedWalletMode(true);
		return () => {
			DaoIWallet.disconnect();
			DaoIWallet.setEmbeddedWalletMode(false);
		};
	}, []);
	useEffect(
		() =>
			installPrivyActions({
				logout: async () => {
					DaoIWallet.disconnect();
					await logout();
				},
			}),
		[logout],
	);
	useEffect(() => {
		if (previousToken.current && previousToken.current !== token) DaoIWallet.setBoundAddress(null);
		if (previousToken.current && !token) {
			clearPendingPrivyLogin();
			void logout().catch(() => {
				/* The DAOI session is already cleared. */
			});
		}
		previousToken.current = token;
	}, [token, logout]);
	useEffect(() => {
		if (ready && (!authenticated || !wallet)) DaoIWallet.disconnect();
	}, [ready, authenticated, wallet]);
	useEffect(() => {
		if (!token || !ready || !authenticated || !boundAddress || !wallet) return;
		let cancelled = false;
		void wallet
			.getEthereumProvider()
			.then(async (provider) => {
				if (
					cancelled ||
					SessionManager.token !== token ||
					DaoIWallet.boundAddress?.toLowerCase() !== wallet.address.toLowerCase()
				)
					return;
				await connectPrivyBusinessWallet(
					provider as InjectedWallet,
					wallet.address,
					() => !cancelled && SessionManager.token === token,
				);
			})
			.catch(() => {
				/* Recovery can be retried from wallet management. */
			});
		return () => {
			cancelled = true;
		};
	}, [token, ready, authenticated, boundAddress, wallet]);
	return null;
});
